import { useRef, useState } from "react";
import { CodePanel } from "@/office/CodePanel";
import { DocEditor } from "@/office/DocEditor";
import { SheetEditor } from "@/office/SheetEditor";
import { StepTimeline } from "@/office/StepTimeline";
import { OfficeProvider, useOffice } from "@/office/office-context";

export function DemoPage() {
  return (
    <OfficeProvider>
      <Workbench />
    </OfficeProvider>
  );
}

function Workbench() {
  const office = useOffice();
  const [ratio, setRatio] = useState(0.5);
  const [dragging, setDragging] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  const module = office.module;
  const fileName = module === "doc" ? `${office.view.doc.title}.wps` : `${office.view.sheet.name}.xlsx`;

  return (
    <div className={`flex h-screen min-h-0 flex-col bg-[#0c131b] ${dragging ? "select-none" : ""}`}>
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-[#16222e] bg-[#0a1118] px-5">
        <span className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-gradient-to-br from-[#5fd0c2] to-[#4f8cff] text-[13px] font-bold text-[#08131a]">
          ◧
        </span>
        <span className="text-[13px] font-medium tracking-wide text-[#e7eef6]">双栏办公台</span>
        <span className="h-4 w-px bg-[#22323f]" />
        <p className="truncate text-[11.5px] text-[#7c93a8]">左侧正常办公，右侧是每一步操作对应的完整源码</p>
        <div className="ml-auto flex shrink-0 items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1.5 text-[#89a0b4]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#37d6a0]" />
            已记录 {office.steps.length} 步
          </span>
          <span className="hidden rounded border border-[#22323f] px-2 py-1 font-mono text-[10.5px] text-[#91a7bb] md:block">
            operation ↔ source
          </span>
        </div>
      </header>

      <div ref={container} className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-col" style={{ width: `${ratio * 100}%` }}>
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-[#e3ded2] bg-white px-3">
            <div className="flex items-center gap-0.5 rounded bg-[#f1efe9] p-0.5">
              <button
                type="button"
                onClick={() => office.setModule("doc")}
                className={`flex h-6 items-center gap-1 rounded px-2.5 text-[12px] transition-colors ${
                  module === "doc" ? "bg-white text-[#1f2328] shadow-sm" : "text-[#7d7568] hover:text-[#1f2328]"
                }`}
              >
                文字
              </button>
              <button
                type="button"
                onClick={() => office.setModule("sheet")}
                className={`flex h-6 items-center gap-1 rounded px-2.5 text-[12px] transition-colors ${
                  module === "sheet" ? "bg-white text-[#1f2328] shadow-sm" : "text-[#7d7568] hover:text-[#1f2328]"
                }`}
              >
                表格
              </button>
            </div>
            <span className="ml-1 truncate font-mono text-[11px] text-[#9a9388]">{fileName}</span>

            {office.replaying ? (
              <div className="ml-auto flex items-center gap-2 text-[11px] text-[#8a5a14]">
                <span>回放第 {office.activeStep?.index ?? 0} 步 · 只读</span>
                <button
                  type="button"
                  onClick={() => office.selectStep(null)}
                  className="rounded border border-[#e2b768] bg-[#fdf3df] px-2 py-0.5 text-[11px] text-[#7a5410] transition-colors hover:bg-[#f9e8c8]"
                >
                  返回最新
                </button>
              </div>
            ) : (
              <span className="ml-auto flex items-center gap-1.5 text-[11px] text-[#8d8679]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#37a06f]" />
                自动记录中
              </span>
            )}
          </div>

          <div className="min-h-0 flex-1">{module === "doc" ? <DocEditor /> : <SheetEditor />}</div>
          <StepTimeline />
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          onPointerDown={(event) => {
            setDragging(true);
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!dragging || !container.current) return;
            const rect = container.current.getBoundingClientRect();
            const next = (event.clientX - rect.left) / rect.width;
            setRatio(Math.min(0.68, Math.max(0.34, next)));
          }}
          onPointerUp={(event) => {
            setDragging(false);
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          className="group relative w-1.5 shrink-0 cursor-col-resize bg-[#16222e]"
        >
          <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-[#22323f] transition-colors group-hover:bg-[#4f8cff]" />
        </div>

        <div className="min-w-0 flex-1">
          <CodePanel />
        </div>
      </div>
    </div>
  );
}
